import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { useMutation, useQueries, useQuery } from '@tanstack/react-query';
import { Chatbot } from 'components/chatbot/chatbot';
import ChildTabBar from 'components/child-tab-bar/child-tab-bar';
import ChildrenHeader from 'components/children-header/children-header';
import ChildrenHome from 'components/children-home/children-home';
import ChildrenSettings from 'components/children-settings/children-settings';
import ChildrenStats from 'components/children-stats/children-stats';
import ChildrenWorkedChapters from 'components/children-worked-chapters/children-worked-chapters';
import LoadingScreen from 'components/loading-screen/loading-screen';
import LoginStreakPopup from 'components/login-streak-popup/login-streak-popup';
import LogoutRangePage from 'components/logout-range-page/logout-range-page';
import Page from 'components/page/page';
import ParentWarningPopup from 'components/parent-warning-popup/parent-warning-popup';
import { useUserContext } from 'contexts/user-context';
import useDisableBackButton from 'hooks/disable-back-button';
import { ChatbotContext, ChatbotContextDataType } from 'contexts/chatbot-context';
import { createContext, useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  getChildren,
  getDailyPoints,
  pushDailyUsage,
  setBlockerConfigured as setBlockerConfiguredFct,
  setFirstLogin,
  updateChildrenPlatform,
} from 'services/childrens';
import BlockerPlugin from 'utils/BlockerPlugin';
import { nowInLogoutRange } from 'utils/date';
import { registerPushNotifications } from 'utils/notifications';
import config from 'config';

/* eslint-disable-next-line */
export interface ChildrenDashboardProps {
  childrenId: string;
}

export const ChildrenHeaderContext = createContext<{ color: string | null; setColor: (color: string | null) => void }>({
  color: null,
  setColor() {},
});

export type ChildrenDashboardTab = 'home' | 'progression' | 'evaluation' | 'stats' | 'settings' | 'chatbot';

export function ChildrenDashboard({ childrenId }: ChildrenDashboardProps) {
  useDisableBackButton();
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState<ChildrenDashboardTab>('home');
  const [chatbotContext, setChatbotContext] = useState<ChatbotContextDataType | null>(null);
  const { parentalControlSettings } = useUserContext();
  const didPushInitialUsage = useRef(false);
  const initialLogicRun = useRef(false);

  const [{ data, isLoading, refetch, isSuccess }] = useQueries({
    queries: [
      {
        queryKey: ['children', childrenId],
        queryFn: () => getChildren(childrenId),
      },
    ],
  });
  const setBlockerConfiguredMutation = useMutation({
    mutationFn: setBlockerConfiguredFct,
    onSuccess: () => refetch(),
  });
  const setFirstLoginMutation = useMutation({
    mutationFn: setFirstLogin,
    onSuccess: () => refetch(),
  });

  useEffect(() => {
    Preferences.set({ key: 'API_URL', value: config.api_baseurl ?? '' });
  }, []);

  useEffect(() => {
    Preferences.get({ key: 'phone_owner' }).then((ownerRes) => {
      if (ownerRes.value === 'parent' || !parentalControlSettings) return;
      BlockerPlugin.propagateParentalControlSettings(parentalControlSettings);
    });
  }, [parentalControlSettings]);

  const { data: dataDailyPoints, refetch: refetchDailyPoints } = useQuery({
    queryKey: ['getDailyPoints'],
    queryFn: () => getDailyPoints(childrenId),
  });
  const updateChildrenPlatformMutation = useMutation({
    mutationFn: updateChildrenPlatform,
  });
  const pushDailyUsageMutation = useMutation({ mutationFn: pushDailyUsage });
  const pushDailyUsageMutateRef = useRef(pushDailyUsageMutation.mutate);
  const [headerColor, setHeaderColor] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const [blockerConfigured, setBlockerConfigured] = useState<boolean>();
  const [parentWarningPopup, setParentWarningPopup] = useState(false);

  const headerContextValue = useMemo(
    () => ({
      color: headerColor,
      setColor: setHeaderColor,
    }),
    [headerColor],
  );

  const chatbotContextValue = useMemo(
    () => ({
      chatbotContext,
      setChatbotContext,
    }),
    [chatbotContext],
  );

  useEffect(() => {
    pushDailyUsageMutateRef.current = pushDailyUsageMutation.mutate;
  }, [pushDailyUsageMutation.mutate]);

  const handleChildLogic = useCallback(() => {
    if (!data) return;
    console.log('[Dashboard] handleChildLogic executing...');

    Preferences.get({ key: 'blockerConfigured' }).then((blockerRes) => {
      if (blockerRes.value !== null) setBlockerConfigured(blockerRes.value === 'true');
      Preferences.get({ key: 'phone_owner' }).then((ownerRes) => {
        if (blockerRes.value === null && ownerRes.value === 'child' && searchParams.get('from_parent') !== 'true') {
          console.log('[Dashboard] Navigating to blocker setup');
          navigate(`/blocker/${data.id}`);
        } else if (data.id) {
          console.log('[Dashboard] Calling registerPushNotifications');
          registerPushNotifications(data.id);

          if (!didPushInitialUsage.current) {
            console.log('[Dashboard] Checking initial daily usage...');
            BlockerPlugin.getDailyUsage().then((res) => {
              console.log(
                `[Dashboard] Calling pushDailyUsageMutation (initial) for child ${data.id} with usage ${res.usage}`,
              );
              pushDailyUsageMutateRef.current({
                id: data.id,
                dailyUsage: res.usage,
              });
              didPushInitialUsage.current = true;
            });
          } else {
            console.log('[Dashboard] Initial daily usage already pushed, skipping mutation.');
          }

          const currentPlatform = Capacitor.getPlatform();
          if (ownerRes.value === 'child' && data.phonePlatform !== currentPlatform) {
            console.log(
              `[Dashboard] Platform changed (${data.phonePlatform} -> ${currentPlatform}). Calling updateChildrenPlatformMutation.`,
            );
            BlockerPlugin.getManufacturer()
              .then((res) => {
                updateChildrenPlatformMutation.mutate({
                  id: data.id,
                  platform: Capacitor.getPlatform(),
                  manufacturer: res.manufacturer,
                });
              })
              .catch(() => {
                updateChildrenPlatformMutation.mutate({
                  id: data.id,
                  platform: Capacitor.getPlatform(),
                });
              });
          }
        }
      });
    });
  }, [data, pushDailyUsageMutateRef, updateChildrenPlatformMutation, searchParams, navigate]);

  useEffect(() => {
    didPushInitialUsage.current = false;
    initialLogicRun.current = false;
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childrenId]);

  useEffect(() => {
    if (isSuccess) {
      console.log('[Dashboard] useEffect[isSuccess] - Query successful. Logic handled by firstLogin effect.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  useEffect(() => {
    if (initialLogicRun.current) {
      console.log('[Dashboard] useEffect[firstLogin] - Initial logic already run, skipping.');
      return;
    }

    if (data && data.id) {
      if (!data.firstLogin) {
        console.log('[Dashboard] useEffect[firstLogin] - Is first login. Calling setFirstLoginMutation...');
        setFirstLoginMutation.mutateAsync(data.id).then(() => {
          console.log('[Dashboard] useEffect[firstLogin] - setFirstLogin success. Now calling handleChildLogic.');
          handleChildLogic();
          initialLogicRun.current = true;
        });
      } else {
        console.log('[Dashboard] useEffect[firstLogin] - Not first login. Calling handleChildLogic.');
        handleChildLogic();
        initialLogicRun.current = true;
      }
    }
    // On garde les dépendances, mais le return au début empêchera les exécutions multiples
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, setFirstLoginMutation, handleChildLogic]);

  useEffect(() => {
    if (data?.lockTime) {
      Preferences.set({
        key: 'lock_time',
        value: data.lockTime.toString(),
      });
    } else {
      Preferences.get({ key: 'lock_time' }).then((res) => {
        if (res.value === null) {
          Preferences.set({ key: 'lock_time', value: '15' });
        }
      });
    }

    Preferences.set({
      key: 'maximumDailyLockTime',
      value: '90',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.lockTime]);

  useEffect(() => {
    Preferences.get({ key: 'phone_owner' }).then((ownerRes) => {
      if (ownerRes.value === 'parent') {
        setParentWarningPopup(true);
      }
    });

    const listener = CapacitorApp.addListener('appStateChange', (event) => {
      if (event.isActive) {
        refetch();
        refetchDailyPoints();
      }
    });

    return () => {
      listener.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (searchParams.get('from_parent') === 'true') {
      setParentWarningPopup(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (blockerConfigured === true && data && data.id && data.configuredBlocker === false) {
      console.log('[Dashboard] useEffect[blockerConfigured] - Calling setBlockerConfiguredMutation');
      setBlockerConfiguredMutation.mutate(data.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockerConfigured, data?.id, data?.configuredBlocker]);

  useEffect(() => {
    if (currentTab === 'chatbot') {
      const isRecent = chatbotContext?.timestamp ? Date.now() - chatbotContext.timestamp < 30000 : false;

      if (chatbotContext && !isRecent) {
        console.log('[Dashboard] Contexte chatbot existant mais trop ancien, réinitialisation.');
        setChatbotContext(null);
      } else if (chatbotContext) {
        console.log('[Dashboard] Contexte chatbot existant et récent.', chatbotContext);
      } else {
        console.log('[Dashboard] Aucun contexte chatbot actif.');
      }
    } else {
      // Optionnel: Réinitialiser le contexte quand on quitte l'onglet chatbot?
      // setChatbotContext(null);
    }
  }, [currentTab, chatbotContext]);

  useEffect(() => {
    console.log('[Dashboard] Setting up periodic usage update interval.');

    const intervalId = setInterval(() => {
      if (data && data.id) {
        console.log('[Dashboard Interval] Fetching daily usage...');
        BlockerPlugin.getDailyUsage()
          .then((res) => {
            console.log(`[Dashboard Interval] Usage fetched: ${res.usage}. Pushing mutation for child ${data.id}.`);
            pushDailyUsageMutateRef.current({
              id: data.id,
              dailyUsage: res.usage,
            });
          })
          .catch((err) => {
            console.error('[Dashboard Interval] Error fetching usage:', err);
          });
      } else {
        console.warn('[Dashboard Interval] Skipping usage update because data or data.id is not available.');
      }
    }, 60000);

    return () => {
      console.log('[Dashboard] Clearing periodic usage update interval.');
      clearInterval(intervalId);
    };
  }, [data]);

  if (isLoading || !data?.id) {
    return <LoadingScreen />;
  }

  if (
    parentalControlSettings?.isLogoutRangeEnabled &&
    parentalControlSettings.logoutRangeMorning &&
    parentalControlSettings.logoutRangeEvening &&
    nowInLogoutRange(parentalControlSettings.logoutRangeMorning, parentalControlSettings.logoutRangeEvening) &&
    blockerConfigured
  ) {
    return <LogoutRangePage />;
  }

  return (
    <ChatbotContext.Provider value={chatbotContextValue}>
      <ChildrenHeaderContext.Provider value={headerContextValue}>
        <Page>
          {currentTab === 'home' && (
            <>
              <ChildrenHeader children={data} />
              <ChildrenHome children={data} blockerConfigured={blockerConfigured} setCurrentTab={setCurrentTab} />
            </>
          )}
          {currentTab === 'progression' && (
            <>
              <ChildrenHeader children={data} />
              <ChildrenWorkedChapters children={data} />
            </>
          )}
          {currentTab === 'chatbot' && data?.id && (
            <Chatbot
              student={data}
              chatbotContext={chatbotContext}
              setChatbotContext={setChatbotContext}
              setCurrentTab={setCurrentTab}
            />
          )}
          {currentTab === 'stats' && <ChildrenStats children={data} />}
          {currentTab === 'settings' && <ChildrenSettings children={data} />}
          <ChildTabBar currentTab={currentTab} setCurrentTab={setCurrentTab} />
          {(dataDailyPoints ?? 0) > 1 && (
            <LoginStreakPopup
              points={dataDailyPoints ?? 0}
              onClose={() => {
                refetchDailyPoints();
                refetch();
              }}
            />
          )}
          {parentWarningPopup && <ParentWarningPopup onClose={() => setParentWarningPopup(false)} />}
        </Page>
      </ChildrenHeaderContext.Provider>
    </ChatbotContext.Provider>
  );
}

export default ChildrenDashboard;
